import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Building2, Star, MapPin, Search, Loader2, TrendingUp, Filter } from 'lucide-react';
import SEO, { getBreadcrumbSchema } from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CompanyCard({ company, index }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star 
        key={i} 
        className={`w-4 h-4 ${i <= Math.round(company.rating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`} 
      />
    );
  }

  const services = company.services || [];
  const displayServices = services.slice(0, 3);
  const moreCount = services.length - 3;

  return (
    <Link to={`/companies/${company.company_id}`}>
      <Card className={`h-full border hover:border-orange-500/50 transition-all ${company.is_promoted ? 'border-orange-500 shadow-lg' : 'border-slate-200'}`}>
        <CardContent className="p-6">
          {company.is_promoted && (
            <Badge className="bg-orange-500 text-white mb-3 flex items-center gap-1 w-fit">
              <TrendingUp className="w-3 h-3" />Promowana
            </Badge>
          )}
          
          <div className="flex items-start gap-4 mb-4">
            <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
              {company.logo_url ? (
                <img src={company.logo_url} alt={company.name} className="w-16 h-16 object-cover" />
              ) : (
                <Building2 className="w-8 h-8 text-slate-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg text-slate-900 truncate">{company.name}</h3>
              {company.location && (
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <MapPin className="w-4 h-4" />{company.location}
                </p>
              )}
            </div>
          </div>

          {company.description && (
            <p className="text-sm text-slate-600 line-clamp-2 mb-4">{company.description}</p>
          )}

          {displayServices.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-4">
              {displayServices.map((service, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">{service}</Badge>
              ))}
              {moreCount > 0 && <Badge variant="secondary" className="text-xs">+{moreCount}</Badge>}
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
            <div className="flex items-center gap-1">
              {stars}
              <span className="font-medium text-slate-900 ml-1">
                {company.rating > 0 ? company.rating.toFixed(1) : '-'}
              </span>
              <span className="text-sm text-slate-500">({company.reviews_count || 0})</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function CategoryItem({ category, selected, onSelect }) {
  return (
    <SelectItem value={category.id}>{category.name}</SelectItem>
  );
}

export default function CompaniesList() {
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    fetch(`${API}/categories`)
      .then(res => res.ok ? res.json() : [])
      .then(setCategories)
      .catch(() => {});
  }, []);

  useEffect(() => {
    async function fetchCompanies() {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (searchLocation) params.append('location', searchLocation);
        if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
        
        const res = await fetch(`${API}/companies?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setCompanies(data.companies || []);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    }

    const debounce = setTimeout(fetchCompanies, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, searchLocation, selectedCategory]);

  const breadcrumbs = [
    { name: 'Strona główna', path: '/' },
    { name: 'Firmy', path: '/companies' }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="companies-list-page">
      <SEO 
        title="Firmy budowlane"
        description={`Przeglądaj ${companies.length}+ sprawdzonych firm budowlanych w całej Polsce. Porównaj opinie, oceny i usługi. Znajdź wykonawcę: elektryka, hydraulika, remonty.`}
        canonical="/companies"
        structuredData={getBreadcrumbSchema(breadcrumbs)}
      />
      
      <div className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading font-bold text-3xl text-white mb-2">Firmy budowlane</h1>
          <p className="text-slate-400 mb-6">Znajdź sprawdzonego wykonawcę w Twojej okolicy</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Szukaj po nazwie firmy..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Szukaj po lokalizacji..."
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <SelectValue placeholder="Wszystkie kategorie" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie kategorie</SelectItem>
                {categories.map(cat => <CategoryItem key={cat.id} category={cat} />)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-bold text-xl text-slate-700 mb-2">Brak firm</h3>
            <p className="text-slate-500">Nie znaleziono firm spełniających kryteria</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companies.map((company, idx) => <CompanyCard key={company.company_id} company={company} index={idx} />)}
          </div>
        )}
      </div>
    </div>
  );
}
